/**
 * <EmptyState /> · Sprint 10 · Operational Excellence.
 *
 * A single reusable empty-state block. Every workspace uses this
 * component so operators encounter identical structure:
 *   • Icon
 *   • Title  ("Nothing here yet.")
 *   • Why    (one sentence — why is this list empty?)
 *   • What to do (one sentence — the next best action)
 *   • Optional CTA button — the primary next action
 *
 * Never renders the phrase "No data." Every empty state must
 * explain Why, What to do, and How to start.
 */
import { Info } from "@phosphor-icons/react";

export default function EmptyState({
  icon: Icon = Info,
  title,
  why,
  next,
  cta,           // { label, onClick, to, testid }
  className = "",
  testid = "empty-state",
}) {
  return (
    <div
      data-testid={testid}
      className={[
        "rounded-lg border border-dashed border-slate-300 bg-slate-50 px-6 py-10",
        "flex flex-col items-center justify-center text-center",
        className,
      ].join(" ")}
    >
      <Icon size={32} weight="regular" className="text-slate-400 mb-3" />
      {title && (
        <div className="text-base font-semibold text-slate-900 mb-1">{title}</div>
      )}
      {why && (
        <p className="text-sm text-slate-600 max-w-md">{why}</p>
      )}
      {next && (
        <p className="text-sm text-slate-500 max-w-md mt-1">{next}</p>
      )}
      {cta && (
        cta.to ? (
          <a
            href={cta.to}
            data-testid={cta.testid || "empty-state-cta"}
            className="mt-4 inline-flex items-center gap-1 rounded-md bg-emerald-600 px-3 py-2 text-sm font-semibold text-white hover:bg-emerald-700"
          >
            {cta.label}
          </a>
        ) : (
          <button
            type="button"
            onClick={cta.onClick}
            data-testid={cta.testid || "empty-state-cta"}
            className="mt-4 inline-flex items-center gap-1 rounded-md bg-emerald-600 px-3 py-2 text-sm font-semibold text-white hover:bg-emerald-700"
          >
            {cta.label}
          </button>
        )
      )}
    </div>
  );
}
